import { Button, Input, Select, Table, TablePaginationConfig } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageHeader } from '@ant-design/pro-components';

import { numberWithCommas } from '../../helpers/number';
import { AppDispatch, RootState } from '../../store/store';

import type { ColumnsType } from 'antd/es/table';
import { smeGlobalSearchThunk } from '../../services/smeApplicationService/smeApplicationThunk';
import { setSmeGlobalSearchValue } from '../../features/smeApplication/smeApplicationSlice';
import { showFinalStatus } from '../../utils/string.util';

const { Search } = Input;

const SmeGlobalSearchPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { globalSearchApplicationList, globalSearchValue } = useSelector(
    (state: RootState) => state.smeApplication
  );

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
    }),
    [pageNum, pageSize]
  );

  const [searchType, setSearchType] = useState(globalSearchValue?.searchType ?? 'applicationNo');

  const resetSearchType = (value: string) => {
    setSearchType(value);
    initialParams.pagination.current = 1;
    initialParams.pagination.pageSize = 10;
    setPaginationState(initialParams.pagination);
  };
  const [searchValue, setSearchValue] = useState(globalSearchValue?.value ?? '');
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: globalSearchApplicationList?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Application No.',
      dataIndex: 'applicationNo',
      key: 'applicationNo',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            const queryParams = new URLSearchParams();
            queryParams.set('readOnly', 'true');
            const newSearch = queryParams.toString();
            navigate({
              pathname: `/sme/application/details/${record?.smeApplicationId}`,
              search: newSearch,
            });
          }}
        >
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Agreement No',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            const queryParams = new URLSearchParams();
            queryParams.set('readOnly', 'true');
            const newSearch = queryParams.toString();
            navigate({
              pathname: `/sme/agreement/details/${record?.smeAgreementId}`,
              search: newSearch,
            });
          }}
        >
          <span style={{ userSelect: 'text' }}>{record?.agreementNo}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'applicantName',
      key: 'applicantName',
    },
    {
      title: 'Organization Type',
      dataIndex: 'organizationType',
      key: 'organizationType',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'applicantNric',
      key: 'applicantNric',
    },
    // {
    //   title: "Application Status",
    //   render: (text, record) => {
    //     return startCase(record?.applicationStatus)
    //   }
    // },
    // {
    //   title: "Agreement Status",
    //   render: (text, record) => {
    //     return startCase(record?.agreement?.agreementStatus)
    //   }
    // },
    {
      title: 'Status',
      render: (text, record) => {
        if (!record) return '-';

        if (record.status === 'DRAFT') {
          return 'Draft_Application';
        }

        if (record.status === 'SUBMITTED_FOR_VERIFIER_2') {
          return 'SUBMITTED FOR VERIFIER 2';
        }

        if (record.status === 'APPROVED' && !record.smeAgreement) {
          return 'APPROVED';
        }

        if (record.smeAgreement) {
          const agreementStatus = record.smeAgreement.status;
          return showFinalStatus(record.status, agreementStatus) ?? '-';
        }

        return showFinalStatus(record.status, null) ?? '-';
      },
    },
    {
      title: 'Stamp Certificate',
      dataIndex: 'eStampStatus',
      key: 'eStampStatus',
      render: (text, record) => {
        return text;
      },
    },
    {
      title: 'Applied Loan Amount',
      dataIndex: 'appliedLoanAmount',
      key: 'appliedLoanAmount',
      render: (text: number) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'approved',
      key: 'approved',
      render: (text, record) =>
        record.status === 'APPROVED' ? (text != null ? `RM ${numberWithCommas(text)}` : '-') : '-',
    },
  ];

  const globalSearch = (requestData?: any) => {
    dispatch(smeGlobalSearchThunk(requestData));
  };

  const saveGlobalSearchValue = async (payload: any) => {
    await dispatch(setSmeGlobalSearchValue(payload));
  };

  const onSearch = async (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      const payload = { searchType, value: trimmedValue };
      setSearchValue(trimmedValue);
      globalSearch({ params: initialParams, payload });
      saveGlobalSearchValue(payload);

      const searchKeys = [
        'applicationNo',
        'agreementNo',
        'companyName',
        'applicantNric',
        'partnerId',
        'staffId',
      ];
      searchKeys.forEach((key) => {
        queryParams.delete(key);
      });

      queryParams.set(searchType, trimmedValue);
      const newSearch = queryParams.toString();
      navigate({
        pathname: location.pathname,
        search: newSearch,
      });
    }
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    globalSearch({
      params: { ...initialParams, pagination },
      payload: { searchType, value: searchValue.trim() },
    });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
    queryParams.set('pageNum', `${pagination.current ?? 1}`);
    queryParams.set('pageSize', `${pagination.pageSize ?? 10}`);
    const newSearch = queryParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  const initialCallbackState = { globalSearch };
  const [callbackState] = useState(initialCallbackState);

  useEffect(() => {
    if (searchValue) {
      callbackState.globalSearch({
        params: initialParams,
        payload: { searchType, value: searchValue.trim() },
      });
    }
  }, [callbackState, initialParams, searchType, searchValue]);

  return (
    <>
      <Helmet>
        <title>Global Search - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Global Search" />
      {/* <Input.Group compact> */}
      {/*  <Select
          defaultValue={searchType}
          style={{ width: 200 }}
          onChange={(value) => setSearchType(value)}
          options={[{ value: "CUSTOMER_NRIC", label: "Applicant NRIC" }]}
        /> */}
      <Input.Group compact>
        <Select
          value={searchType}
          style={{ width: 200 }}
          onChange={(value) => resetSearchType(value)}
          options={[
            { value: 'applicationNo', label: 'Application No' },
            { value: 'agreementNo', label: 'Agreement No' },
            { value: 'companyName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' },
            { value: 'partnerId', label: 'Partner ID' },
            { value: 'staffId', label: 'Staff ID' },
          ]}
        />
        <Search placeholder="Input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>

      {/* </Input.Group> */}
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={globalSearchApplicationList?.data?.data}
        loading={globalSearchApplicationList?.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default SmeGlobalSearchPage;
